import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApp } from '../contexts/AppContext';
import { Sun, Moon, ChevronLeft, ChevronRight, Play, HelpCircle } from 'lucide-react';
import ShikshaLogo from '../components/ShikshaLogo';
import GovLogo from '../components/GovLogo';
import GlobalSearch from '../components/GlobalSearch';
import AlertsBell from '../components/AlertsBell';
import SidebarNav from '../components/SidebarNav';
import { findNav } from '../navConfig';

export default function AdminLayout({ children, onStartDemo }) {
  const { t, lang, toggleLang, role, setRole, ROLES, toggleTheme, isDark } = useApp();
  const navigate = useNavigate();
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);

  const roleLabels = { teacher: t('role_teacher'), principal: t('role_principal'), district: t('role_district'), moe: t('role_moe') };




  return (
    <div style={{ minHeight: '100vh', background: 'var(--color-background)' }}>
      {/* Sidebar */}
      <aside className={`admin-sidebar transition-all duration-200 ${collapsed ? 'w-16' : 'w-60'}`} data-testid="admin-sidebar">
        {/* Logo — acts as Home button */}
        <div style={{ padding: collapsed ? 'var(--space-4) var(--space-2)' : 'var(--space-5) var(--space-4)', borderBottom: '1px solid var(--color-separator)' }}>
          {collapsed ? (
            <GovLogo size={32} />
          ) : (
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <GovLogo size={36} />
              <ShikshaLogo size={32} showText={true} onClick={() => navigate('/')} />
            </div>
          )}
        </div>

        <SidebarNav collapsed={collapsed} testPrefix="sidebar" />

        <button onClick={() => setCollapsed(!collapsed)}
          className="absolute bottom-4 left-0 right-0 flex justify-center"
          style={{ color: 'var(--color-label-tertiary)' }}>
          {collapsed ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
        </button>
      </aside>

      {/* Main content area */}
      <div className={`transition-all duration-200`} style={{ marginLeft: collapsed ? 64 : 240 }}>
        {/* Frosted Glass Top Bar */}
        <header className="nav-header" data-testid="admin-header">
          <div className="flex items-center justify-between w-full">
            <div style={{ textAlign: 'left' }}>
              <h2 style={{ fontSize: 'var(--text-title2)', fontWeight: 600, letterSpacing: '-0.02em', color: 'var(--color-label-primary)', textAlign: 'left' }}>
                {(() => { const item = findNav(location.pathname); return item ? (lang === 'bn' ? item.bn : item.en) : 'Home'; })()}
              </h2>
              <p style={{ fontSize: '11px', color: 'var(--color-label-tertiary)', fontWeight: 400, marginTop: 2, textAlign: 'left' }}>
                {(() => { const item = findNav(location.pathname); return item ? (lang === 'bn' ? item.en : item.bn) : 'হোম'; })()}
              </p>
            </div>
            <div className="flex items-center" style={{ gap: 'var(--space-3)', marginLeft: 'auto' }}>
              <GlobalSearch />
              <AlertsBell />
              <a href="/USER_GUIDE.md" target="_blank" rel="noreferrer" data-testid="user-guide-btn"
                className="btn btn-sm" style={{ background: 'var(--color-surface-2)', color: 'var(--color-label-secondary)', padding: '5px 12px', fontSize: 'var(--text-footnote)', textDecoration: 'none', gap: 'var(--space-1)' }}>
                <HelpCircle size={13} />
                {lang === 'bn' ? 'গাইড' : 'User Guide'}
              </a>
              {onStartDemo && (
                <button data-testid="topbar-demo-btn" onClick={onStartDemo}
                  className="btn btn-sm" style={{ background: 'var(--color-red)', color: 'white', padding: '5px 12px', fontSize: 'var(--text-footnote)', fontWeight: 600, gap: 'var(--space-1)' }}>
                  <Play size={13} />
                  {lang === 'bn' ? 'ডেমো' : 'Demo Walkthrough'}
                </button>
              )}
              <div className="flex items-center gap-1.5" style={{ fontSize: 'var(--text-footnote)', color: 'var(--color-label-secondary)' }}>
                <span className="status-dot status-connected" />
                <span>{lang === 'bn' ? 'সিঙ্ক হয়েছে' : 'Synced'}</span>
              </div>
              <button data-testid="theme-toggle" onClick={toggleTheme}
                className="btn btn-ghost btn-sm" style={{ padding: '6px 10px' }}
                title={isDark ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
                {isDark ? <Sun size={16} /> : <Moon size={16} />}
              </button>
              <select data-testid="role-switcher" value={role} onChange={(e) => setRole(e.target.value)}
                className="input-filled text-[13px] py-1.5 px-3" style={{ width: 'auto', minWidth: 120 }}>
                {ROLES.map(r => <option key={r} value={r}>{roleLabels[r]}</option>)}
              </select>
              <button data-testid="language-toggle" onClick={toggleLang}
                className="btn btn-ghost btn-sm font-bangla">
                {lang === 'bn' ? 'EN' : 'বাংলা'}
              </button>
            </div>
          </div>
        </header>

        <main style={{ padding: 'var(--space-8) var(--space-6)' }}>
          <div className="animate-reveal">
            {children}
          </div>
        </main>
      </div>
    </div>
  );
}
